import React, { useState } from "react";
import { IconButton, InputAdornment, TextField, useTheme } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/**
 * A reusable text box component that can be used for various text inputs.
 *
 * @param {string} value **Required** The current value of the text box.
 * @param {function} handleChange **Required** A function to handle changes to the text box value.
 * @param {boolean} longEntry Whether the text box should be a long entry field.
 * @param {boolean} error Whether the text box should be displayed as an error field.
 * @param {string} helperText Helper text to display below the text box.
 * @param {string} placeholder Placeholder text to display in the text box.
 * @param {string} type The type of input field (currently only used for 'password').
 * @param {boolean} disabled Whether the text box should be disabled.
 * @param {boolean} borderVariant Whether the text box should have a different border style.
 * @param {customStyles} customStyles Custom styles to apply to the text box.
 * @returns {JSX.Element} A text box component.
*/

const TextBox = ({ 
  value, 
  handleChange, 
  longEntry = false, 
  error = false, 
  helperText = "", 
  placeholder = "", 
  type = "text", 
  disabled = false, 
  borderVariant = false,
  labelId = "",
  customStyles = {},
  ...props 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const getInputStyles = () => {
    const styles = {
      borderRadius: "6px",
      paddingLeft: "5px",
      paddingTop: "8px",
      paddingBottom: "7px",
      opacity: 1,
      border: customStyles.border || `1px solid #E9ECEE`,
      '&.Mui-focused': {
        border: '1px solid #00A6FF',
        backgroundColor: '#fff',
        opacity: 1,
      },
      '&.Mui-disabled': {
        border: '1px solid #C6CCD2',
        color: '#C6CCD2',
        opacity: 1,
      },
      '& .MuiInputBase-input': {
        padding: 0,
      },
      '& .MuiInputBase-input::placeholder': {
        color: '#C6CCD2',
        opacity: 1,
      },
      ...customStyles
    };

    if (borderVariant) {
      styles.border = `1px solid #6F8090`;
    }

    if (longEntry) {
      styles.border = "none";
      styles.color = "#707070";
      styles.backgroundColor = "#F3F4F5";
      styles.paddingTop = "12px";
      styles.paddingLeft = "14px";
      styles.paddingBottom = "11px";
    }

    if (error) {
      styles.border = `1px solid #F5325B`;
      styles["&.Mui-focused"].border = `1px solid #F5325B`;
    }

    return styles;
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <TextField
      {...props}
      type={type === "password" && !showPassword ? "password" : "text"}
      variant="standard"
      value={value}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      multiline={longEntry}
      maxRows={longEntry ? 4 : 1}
      placeholder={placeholder}
      disabled={disabled}
      fullWidth
      InputProps={{
        disableUnderline: true,
        endAdornment:
          type === "password" ? (
            <InputAdornment position="end">
              <IconButton onClick={handleShowPassword}>
                {!showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ) : null,
        sx: {
          ...theme.typography.body2,
          ...getInputStyles(),
        },
      }}
      FormHelperTextProps={{
        sx: {
          fontSize: '10px',
          fontFamily: 'Lato',
          fontWeight: 'regular',
          marginTop: '4px',
        }
      }}
    />
  );
};

export default TextBox;